import { Component, inject, input, InputSignal, ViewChild } from '@angular/core';
import { MatMenu, MatMenuItem } from '@angular/material/menu';

import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { SprayTypes } from 'src/app/shared/models';
import { VirusMonitorSignalStore } from '../signal-store/virus-monitor-signal-store';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';
import { createVirusMonitorTelemetryProperties, VirusMonitorAIIdentifiers } from './constants';
import { AppState } from '@app/shared/models';

@Component({
  selector: 'app-virus-monitor-action-menu',
  template: `
    <mat-menu #actionMenu="matMenu">
      <button mat-menu-item (click)="onSprayClicked()">
        {{ 'virusMonitor.actionMenu.spray' | translate }}
      </button>
      <button mat-menu-item (click)="onIrrigateClicked()">
        {{ 'virusMonitor.actionMenu.irrigation' | translate }}
      </button>
    </mat-menu>
  `,
  imports: [
    MatMenu,
    MatMenuItem,
    TranslatePipe,
  ],
})
export class VirusMonitorActionMenuComponent {
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly virusMonitorStore = inject(VirusMonitorSignalStore);
  private readonly locale = inject(LocaleService);

  @ViewChild('actionMenu', { static: true }) public menu!: MatMenu;

  protected sprayTypes: typeof SprayTypes = SprayTypes;

  readonly advices: InputSignal<CropFieldAdvice[]> = input.required<CropFieldAdvice[]>();
  readonly actionDate: InputSignal<Date | undefined> = input<Date | undefined>();
  readonly context = input<'overview' | 'insights' | undefined>(undefined);

  protected onSprayClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('spray');
    this.virusMonitorStore.openSpraysDialog(
      SprayTypes.PreventiveSpray,
      this.advices(),
      this.actionDate(),
    );
  }

  protected onIrrigateClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackActionMenuItemSelected('irrigation');
    this.virusMonitorStore.openBlightIrrigation(this.advices(), this.actionDate());
  }

  private trackActionMenuItemSelected(actionType: string): void {
    const context = this.context();
    if (!context) {
      return;
    }

    const eventIdentifier =
      context === 'insights'
        ? VirusMonitorAIIdentifiers.insightsActionMenuItemSelected
        : VirusMonitorAIIdentifiers.overviewActionMenuItemSelected;

    this.ai.trackEvent(
      eventIdentifier,
      createVirusMonitorTelemetryProperties('actionMenuItemSelected', {
        actionType,
      }),
    );
  }

  private notifyMutationUnavailable(): void {
    this.appStore.dispatch(
      NotificationActions.infoSnackbar({
        message: this.locale.translate('UI.CropYearUnavailableMessage'),
      }),
    );
  }
}
